import { useState } from "react";
import { Link, useNavigate } from "react-router";

import { ApiError } from "@/api/errors";
import { useCompletePasswordReset, usePasswordResetPreview } from "@/api/queries/auth";
import { Button, Field, InlineError, Input, PageLoader } from "@/components/ui";
import { formError } from "@/hooks/useFormErrors";
import { formatDateTime } from "@/lib/format";
import { useFragmentToken } from "@/lib/fragmentToken";

import { AuthFrame } from "./AuthFrame";

const signInLink = (
  <Link className="font-medium text-accent-text hover:underline" to="/login">
    Go to sign in
  </Link>
);

/** Choose a new password with a single-use link created by Kasten. The token lives in the URL fragment. */
export function ResetPasswordPage() {
  const token = useFragmentToken();
  const preview = usePasswordResetPreview(token);
  const complete = useCompletePasswordReset();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [mismatch, setMismatch] = useState(false);

  if (!token) {
    return (
      <AuthFrame footer={signInLink}>
        <h1 className="text-lg font-semibold">Reset link incomplete</h1>
        <p className="mt-1 text-sm text-fg-muted">Open the full link you were sent, including everything after the “#”.</p>
      </AuthFrame>
    );
  }
  if (preview.isLoading) return <PageLoader label="Checking reset link" />;
  const data = preview.data;
  const invalid = !data?.valid || (complete.error instanceof ApiError && complete.error.code === "reset_link_invalid");
  if (invalid) {
    return (
      <AuthFrame footer={signInLink}>
        <h1 className="text-lg font-semibold">This reset link is no longer valid</h1>
        <p className="mt-1 text-sm text-fg-muted">
          It may have expired, been used already or been replaced by a newer link. Ask Kasten support for a new one.
        </p>
      </AuthFrame>
    );
  }

  const passwordError = complete.error instanceof ApiError ? complete.error.fieldError("password") : undefined;
  return (
    <AuthFrame footer={signInLink}>
      <h1 className="text-lg font-semibold text-fg">Choose a new password</h1>
      <p className="mt-0.5 mb-4 text-sm text-fg-muted">
        For <span className="font-medium text-fg">{data.email}</span>. This link works once, until {formatDateTime(data.expires_at)}.
      </p>
      <form
        noValidate
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (password !== confirm) return setMismatch(true);
          setMismatch(false);
          complete.mutate({ token, password }, { onSuccess: () => navigate("/", { replace: true }) });
        }}
      >
        {!passwordError && <InlineError error={formError(complete.error)} />}
        <Field label="Email">
          <Input value={data.email ?? ""} readOnly disabled autoComplete="username" />
        </Field>
        <Field label="New password" hint="At least 10 characters; avoid common passwords." error={passwordError}>
          <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus />
        </Field>
        <Field label="Confirm new password" error={mismatch ? "The passwords don't match." : undefined}>
          <Input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
        </Field>
        <p className="text-xs text-fg-subtle">You'll be signed out of every other browser and device, and signed in here.</p>
        <Button type="submit" variant="primary" loading={complete.isPending} disabled={!password || !confirm}>
          Set password and sign in
        </Button>
      </form>
    </AuthFrame>
  );
}
